import { Actor, Canvas, Engine, Random, vec } from 'excalibur';
import { getTheme } from '../themes/theme';
import { COMBO_FIRE_MULTIPLIER, SEEDS } from '../utils/constants';
import { HAND_FONT } from '../utils/fonts';
import { css, drawFlame, sketchStroke, wobblyLinePath } from '../utils/sketch';

export class ComboMeter extends Actor {
  private multiplier = 1;
  private time = 0;
  private popTimer = 0;

  constructor() {
    super({
      name: 'combo-meter',
      pos: vec(360, 372),
      z: 25,
      graphic: new Canvas({
        width: 260,
        height: 132,
        cache: false,
        draw: (ctx) => this.paint(ctx),
      }),
    });
  }

  setMultiplier(multiplier: number): void {
    if (multiplier === this.multiplier) {
      return;
    }
    this.multiplier = multiplier;
    this.popTimer = 0.35;
  }

  reset(): void {
    this.multiplier = 1;
    this.popTimer = 0;
    this.scale = vec(1, 1);
  }

  onPreUpdate(_engine: Engine, elapsed: number): void {
    const dt = elapsed / 1000;
    this.time += dt;
    if (this.popTimer > 0) {
      this.popTimer = Math.max(0, this.popTimer - dt);
      const progress = 1 - this.popTimer / 0.35;
      const scale = 1 + Math.sin(Math.PI * progress) * 0.16;
      this.scale = vec(scale, scale);
      if (this.popTimer === 0) {
        this.scale = vec(1, 1);
      }
    }
  }

  private paint(ctx: CanvasRenderingContext2D): void {
    const palette = getTheme().palette;
    const rng = new Random(SEEDS.combo);
    const midX = 130;
    const midY = 58;
    const onFire = this.multiplier >= COMBO_FIRE_MULTIPLIER;

    if (onFire) {
      const flames = 4 + (this.multiplier - COMBO_FIRE_MULTIPLIER) * 2;
      for (let i = 0; i < flames; i++) {
        const t = (i + 0.5) / flames;
        const x = midX - 74 + t * 148 + rng.floating(-5, 5);
        const flicker = 1 + Math.sin(this.time * (4.5 + i * 0.7) + i) * 0.22;
        const height = (44 + rng.floating(6, 26)) * flicker;
        drawFlame(
          ctx,
          x,
          midY + 42,
          rng.floating(-0.16, 0.16),
          height,
          28 + rng.floating(2, 12),
          rng,
          palette.flame,
          palette.ember,
          0.9,
        );
      }
    }

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `${onFire ? 86 : 72}px ${HAND_FONT}`;
    ctx.fillStyle = css(onFire ? palette.flameDeep : this.multiplier > 1 ? palette.blueDeep : palette.inkSoft);
    ctx.fillText(`${this.multiplier}x`, midX, midY);
    ctx.restore();

    sketchStroke(
      ctx,
      rng,
      { color: onFire ? palette.ember : palette.inkSoft, width: 3.4, alpha: 0.7 },
      (c) => wobblyLinePath(c, midX - 52, midY + 46, midX + 52, midY + 46, rng, 3, 10),
      1,
    );
  }
}
